import { apiRequest, apiUpload, API_BASE } from './api'

export interface UserProfile {
  id: string
  memberId?: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  phoneNumber?: string
  profilePicture?: string
  role: string
  isVerified: boolean
  isApproved: boolean
  createdAt: string
  updatedAt: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  dateOfBirth?: string
  gender?: 'MALE' | 'FEMALE'
  preferences?: Record<string, any>
  nationality?: string
  passportNumber?: string
  preferredBurialLocation?: string
  preferredBurialCountry?: string
  walletNumber?: string | null
  virtualAccountNumber?: string | null
  virtualAccountBank?: string | null
  /** Informational VA balance (RM) — null = never set. Read-only for the member. */
  virtualAccountBalance?: number | null
  /** This user's own 6-char referral code — share it, others enter it at signup. */
  referralCode?: string
}

export interface UpdateProfileData {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  dateOfBirth?: string
  gender?: 'MALE' | 'FEMALE'
  preferences?: Record<string, any>
  email?: string
  nationality?: string
  passportNumber?: string
  preferredBurialLocation?: string
  preferredBurialCountry?: string
}

export interface ChangePasswordData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface ProfileCompletion {
  completionPercentage: number
  completedFields: number
  totalFields: number
  missingFields: number
}

export const profileService = {
  // Get user profile
  async getProfile(): Promise<UserProfile> {
    const response = await apiRequest.get<UserProfile>('/profile')
    return response.data
  },

  // Update user profile
  async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
    const response = await apiRequest.put<UserProfile>('/profile', data)
    return response.data
  },

  // Upload profile picture
  async uploadProfilePicture(file: File): Promise<UserProfile> {
    const formData = new FormData()
    formData.append('file', file)

    // apiUpload, not apiRequest.post — the latter drops its config argument,
    // which silently downgraded this to a JSON request (400 "No file uploaded").
    const response = await apiUpload<UserProfile>('/profile/picture', formData)
    return response.data
  },

  // Get profile picture URL
  getProfilePictureUrl(userId: string): string {
    // API_BASE (not raw VITE_API_URL) so dev gets the same-origin /api path
    // proxied by Vite, matching how every other request is built.
    return `${API_BASE}/api/profile/picture/${userId}`
  },

  // Delete profile picture
  async deleteProfilePicture(): Promise<{ message: string }> {
    const response = await apiRequest.delete<{ message: string }>('/profile/picture')
    return response.data
  },

  // Change password
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await apiRequest.post<{ message: string }>('/profile/change-password', data)
    return response.data
  },

  // Get profile completion status
  async getProfileCompletion(): Promise<ProfileCompletion> {
    const response = await apiRequest.get<ProfileCompletion>('/profile/completion')
    return response.data
  },
}